import React, { useMemo, useState } from 'react';
import { Pager } from '@/components/Pager';
import { CardMenuButton } from '@/components/CardMenu';
import { usePagination, useBreakpoint } from '@/hooks';
import styles from './BuilderTable.module.scss';

export interface BuilderTableColumn<T> {
  key: string;
  title: string;
  sortFn?: (a: T, b: T) => number;
  render: (record: T) => React.ReactNode;
}

interface BuilderTableProps<T> {
  columns: BuilderTableColumn<T>[];
  /** CSS grid-template-columns value — one fraction/width per column. */
  gridTemplateColumns: string;
  data: T[];
  rowKey: (record: T) => string;
  pageSize: number;
  onRowClick?: (record: T) => void;
  emptyTitle: string;
  emptySub: string;
  defaultSortKey?: string;
  loading?: boolean;
  onMobileDetails?: (record: T) => void;
  mobileActionsLabel?: (record: T) => string;
}

/**
 * Panel/thead/trow CSS-grid table — sortable column headers with a
 * text arrow indicator, and a matching pager. Owns its own sort + page state
 * so pages only need to pass the filtered dataset.
 */
export function BuilderTable<T>({
  columns,
  gridTemplateColumns,
  data,
  rowKey,
  pageSize,
  onRowClick,
  emptyTitle,
  emptySub,
  defaultSortKey,
  loading,
  onMobileDetails,
  mobileActionsLabel,
}: BuilderTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSortKey);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const { isMobile } = useBreakpoint();

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortFn) return data;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => dir * col.sortFn!(a, b));
  }, [data, columns, sortKey, sortDir]);

  const { page, pageCount, pageItems: pageRows, from, to, total, setPage } = usePagination(
    sorted,
    pageSize,
  );

  const toggleSort = (col: BuilderTableColumn<T>) => {
    if (!col.sortFn) return;
    if (sortKey === col.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(col.key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const arrow = (key: string) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  if (loading) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyTitle}>Loading…</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyTitle}>{emptyTitle}</div>
        <div className={styles.emptySub}>{emptySub}</div>
      </div>
    );
  }

  const actionsCol = columns.find((col) => col.key === 'actions');
  const fieldCols = columns.filter((col) => col.key !== 'actions');
  const [titleCol, ...restCols] = fieldCols;
  const cardFieldCols = onMobileDetails ? restCols : fieldCols;

  return (
    <>
      {isMobile ? (
        <div className={styles.mobileList}>
          {pageRows.map((record) => (
            <div
              key={rowKey(record)}
              className={`${styles.mobileCard} ${onRowClick ? styles.mobileCardClick : ''}`}
              onClick={() => onRowClick?.(record)}
            >
              {onMobileDetails && titleCol && (
                <div className={styles.mobileHead}>
                  <div className={styles.mobileTitle}>{titleCol.render(record)}</div>
                  <div className={styles.mobileMenu}>
                    <CardMenuButton
                      haspopup="dialog"
                      ariaLabel={mobileActionsLabel ? mobileActionsLabel(record) : 'More actions'}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMobileDetails(record);
                      }}
                    />
                  </div>
                </div>
              )}
              <div className={styles.mobileFields}>
                {cardFieldCols.map((col) => (
                  <div className={styles.mobileField} key={col.key}>
                    <span className={styles.mobileFieldLabel}>{col.title}</span>
                    <span className={styles.mobileFieldValue}>{col.render(record)}</span>
                  </div>
                ))}
              </div>
              {!onMobileDetails && actionsCol && (
                <div className={styles.mobileActions}>{actionsCol.render(record)}</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.panel}>
          <div className={styles.rows}>
            <div className={styles.thead} style={{ gridTemplateColumns }}>
              {columns.map((col) => (
                <div
                  key={col.key}
                  className={`${col.sortFn ? styles.thSort : ''} ${sortKey === col.key ? styles.thSortActive : ''}`}
                  onClick={() => toggleSort(col)}
                >
                  {col.title}
                  {arrow(col.key)}
                </div>
              ))}
            </div>
            {pageRows.map((record) => (
              <div
                key={rowKey(record)}
                className={`${styles.trow} ${onRowClick ? styles.trowClick : ''}`}
                style={{ gridTemplateColumns }}
                onClick={() => onRowClick?.(record)}
              >
                {columns.map((col) => (
                  <div key={col.key}>{col.render(record)}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      {sorted.length > pageSize && (
        <Pager page={page} pageCount={pageCount} from={from} to={to} total={total} onChange={setPage} />
      )}
    </>
  );
}

export default BuilderTable;
